import type { RestDataResponseNextBestActionResponse } from '@cecm-microservices-sdk/core/services/account/next-best-action';
import type { SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import type { SnapshotAlertItem } from './SnapshotAlertPopover';
import { buildCareRouteUrl } from './careAccountUrls';

export type NextBestAction = NonNullable<RestDataResponseNextBestActionResponse['data']['actions']>[number];

const MAX_DISPLAYED_ALERTS = 3;
const PLAYER_WELCOME_PATH = '/player/welcome';
const PLANS_PATH = '/plans';

export interface MappedAlerts {
    featuredAlert?: SnapshotAlertBannerProps;
    alerts: SnapshotAlertItem[];
    badgeCount: number;
    alertTypes: string[];
}

type ActionTone = 'critical' | 'warning' | 'default';

const actionTonePriority: Record<ActionTone, number> = {
    critical: 0,
    warning: 1,
    default: 2,
};

function actionTone(action: NextBestAction): ActionTone {
    switch (action.type) {
        case 'PAYMENT':
            return 'critical';
        case 'PAYMENT_REMINDER':
            return 'warning';
        default:
            return 'default';
    }
}

function buildMessage(action: NextBestAction): string {
    switch (action.type) {
        case 'PAYMENT':
            return 'Your payment is past due';
        case 'PAYMENT_REMINDER':
            return 'You have a payment reminder on your account.';
        case 'CREDENTIALS':
            return 'Set up your streaming credentials to listen across devices.';
        case 'CONTENT':
            return action.favoriteChannel
                ? `Pick up where you left off with ${action.favoriteChannel}.`
                : 'Jump back in and keep listening to your SiriusXM favorites.';
        case 'DEVICES':
            return 'Keep your devices ready for listening at home and on the go.';
        case 'UPGRADE':
            return 'See options to upgrade your SiriusXM plan.';
        case 'REACTIVATE':
            return 'Reactivate your SiriusXM subscription and start listening again.';
        case 'CONVERT':
            return action.noOfDaysLeftInTrial != null
                ? `Your trial has ${action.noOfDaysLeftInTrial} days left. Keep your subscription going.`
                : 'Continue your SiriusXM subscription without interruption.';
        case 'SC_AC':
            return 'Review an update on your SiriusXM account.';
        case 'PRICE_MSG':
            return 'Review the latest pricing information for your subscription.';
        default:
            return 'Review the latest update on your SiriusXM account.';
    }
}

function buildAlertId(action: NextBestAction, index: number): string {
    return `${action.type ?? 'UNKNOWN'}-${action.subscriptionId ?? action.last4DigitsOfRadioId ?? index}`;
}

function buildAlertCta(
    action: NextBestAction,
): Pick<SnapshotAlertItem, 'ctaHref' | 'ctaLabel'> & Partial<Pick<SnapshotAlertBannerProps, 'ctaHref' | 'ctaLabel'>> {
    switch (action.type) {
        case 'PAYMENT':
            return {
                ctaHref: buildCareRouteUrl('billing'),
                ctaLabel: 'Make a payment now',
            };
        case 'PAYMENT_REMINDER':
            return {
                ctaHref: buildCareRouteUrl('billing'),
                ctaLabel: 'Make a payment',
            };
        case 'CREDENTIALS':
            return {
                ctaHref: buildCareRouteUrl('accountCredentials'),
                ctaLabel: 'Set up now',
            };
        case 'CONTENT':
            return {
                ctaHref: PLAYER_WELCOME_PATH,
                ctaLabel: 'Listen now',
            };
        case 'DEVICES':
            return {
                ctaHref: buildCareRouteUrl('accountManage'),
                ctaLabel: 'Manage account',
            };
        case 'UPGRADE':
            return {
                ctaHref: PLANS_PATH,
                ctaLabel: 'View plans',
            };
        case 'REACTIVATE':
            return {
                ctaHref: buildCareRouteUrl('streamingPurchase'),
                ctaLabel: 'Reactivate now',
            };
        case 'CONVERT':
            return {
                ctaHref: buildCareRouteUrl('streamingPurchase'),
                ctaLabel: 'Continue subscription',
            };
        case 'SC_AC':
            return {
                ctaHref: buildCareRouteUrl('accountManage'),
                ctaLabel: 'Manage account',
            };
        case 'PRICE_MSG':
            return {
                ctaHref: PLANS_PATH,
                ctaLabel: 'View pricing',
            };
        default:
            return {
                ctaHref: buildCareRouteUrl('accountManage'),
                ctaLabel: 'View details',
            };
    }
}

function toAnalyticsAlertType(action: NextBestAction): string {
    return action.type === 'PAYMENT' ? 'PAST_DUE' : (action.type ?? 'UNKNOWN');
}

export function nbaToAlertItems(actions: NextBestAction[]): MappedAlerts {
    const alertTypes = actions.map(toAnalyticsAlertType);

    const criticalIndex = actions.findIndex((a) => actionTone(a) === 'critical');

    if (criticalIndex >= 0) {
        const criticalAction = actions[criticalIndex];
        const cta = buildAlertCta(criticalAction);

        return {
            featuredAlert: {
                ...cta,
                tone: 'critical',
                message: buildMessage(criticalAction),
            },
            alerts: [],
            badgeCount: 1,
            alertTypes,
        };
    }

    let featuredAlert: SnapshotAlertBannerProps | undefined;
    const remaining: NextBestAction[] = [];
    const warningIndex = actions.findIndex((a) => actionTone(a) === 'warning');

    actions.forEach((action, index) => {
        if (index === warningIndex) {
            const cta = buildAlertCta(action);
            featuredAlert = {
                ...cta,
                tone: 'warning',
                message: buildMessage(action),
            };
        } else {
            remaining.push(action);
        }
    });

    const maxListItems = MAX_DISPLAYED_ALERTS - (featuredAlert ? 1 : 0);
    const alerts: SnapshotAlertItem[] = remaining
        .map((action, index) => ({ action, index, tone: actionTone(action) }))
        .sort((a, b) => actionTonePriority[a.tone] - actionTonePriority[b.tone] || a.index - b.index)
        .slice(0, maxListItems)
        .map(({ action, index, tone }) => ({
            ...buildAlertCta(action),
            ...(tone === 'default' ? {} : { tone }),
            id: buildAlertId(action, index),
            message: buildMessage(action),
        }));
    const badgeCount = alerts.length + (featuredAlert ? 1 : 0);

    return { featuredAlert, alerts, badgeCount, alertTypes };
}
